import React from "react";

export const T = {
  bg: '#FAFBFC', surface: '#FFFFFF', surfaceAlt: '#F5F6F8',
  border: '#E2E8F0', borderLight: '#EDF2F7',
  text: '#1A202C', textSecondary: '#4A5568', textMuted: '#A0AEC0',
  blue: '#3182CE', blueBg: '#EBF4FF', blueBorder: '#90CDF4',
  green: '#38A169', greenBg: '#F0FFF4', greenBorder: '#9AE6B4',
  amber: '#D69E2E', amberBg: '#FFFAF0', amberBorder: '#F6E05E',
  red: '#E53E3E', redBg: '#FFF5F5', redBorder: '#FEB2B2',
  purple: '#805AD5', purpleBg: '#FAF5FF', purpleBorder: '#D6BCFA',
  sidebar: '#1E293B', sidebarText: '#94A3B8', sidebarActive: '#FFFFFF',
};

export const font = "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
export const mono = "'SF Mono','Cascadia Code',Consolas,monospace";

export const fmtCcy = (v, c = 'GBP') => {
  const s = { GBP: '\u00a3', USD: '$', EUR: '\u20ac' }[c] || c + ' ';
  return `${v < 0 ? '-' : ''}${s}${Math.abs(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
export const fmtPct = v => `${((v || 0) * 100).toFixed(2)}%`;
export const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';
export const fmtDateTime = d => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '\u2014';

export const Badge = ({ children, color = 'blue' }) => {
  const c = { blue: { bg: T.blueBg, t: T.blue, b: T.blueBorder }, green: { bg: T.greenBg, t: T.green, b: T.greenBorder }, amber: { bg: T.amberBg, t: T.amber, b: T.amberBorder }, red: { bg: T.redBg, t: T.red, b: T.redBorder }, purple: { bg: T.purpleBg, t: T.purple, b: T.purpleBorder }, gray: { bg: T.surfaceAlt, t: T.textSecondary, b: T.border } }[color] || { bg: T.blueBg, t: T.blue, b: T.blueBorder };
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: c.bg, color: c.t, border: `1px solid ${c.b}` }}>{children}</span>;
};

export const Card = ({ children, style: s, onClick }) => <div onClick={onClick} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 20, ...s, ...(onClick ? { cursor: 'pointer' } : {}) }}>{children}</div>;

export const Btn = ({ children, onClick, variant = 'primary', disabled, small, style: s }) => {
  const v = { primary: { bg: T.blue, t: '#fff', b: T.blue }, secondary: { bg: T.surface, t: T.text, b: T.border }, danger: { bg: T.red, t: '#fff', b: T.red }, ghost: { bg: 'transparent', t: T.textSecondary, b: 'transparent' } }[variant] || { bg: T.blue, t: '#fff', b: T.blue };
  return <button disabled={disabled} onClick={onClick} style={{ padding: small ? '4px 12px' : '8px 18px', borderRadius: 6, fontSize: small ? 12 : 13, fontWeight: 500, fontFamily: font, border: `1px solid ${v.b}`, background: v.bg, color: v.t, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, ...s }}>{children}</button>;
};

export const SectionTitle = ({ children }) => <h3 style={{ fontSize: 14, fontWeight: 600, color: T.text, margin: '0 0 12px 0', fontFamily: font }}>{children}</h3>;

const placeholderStyle = `
  input::placeholder, textarea::placeholder {
    color: #A0AEC0 !important;
    font-style: italic !important;
    opacity: 1 !important;
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('corebank-placeholder-style')) {
  const style = document.createElement('style');
  style.id = 'corebank-placeholder-style';
  style.textContent = placeholderStyle;
  document.head.appendChild(style);
}

export const Input = ({ label, value, onChange, placeholder, disabled, multiline, type = 'text', ...rest }) => (
  <div style={{ marginBottom: 12 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: T.textSecondary, marginBottom: 4 }}>{label}</label>}
    {multiline
      ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} rows={3} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: font, color: T.text, background: disabled ? T.surfaceAlt : T.surface, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }} />
      : <input type={type} value={value} onChange={typeof onChange === 'function' ? (rest.raw ? onChange : e => onChange(e.target.value)) : undefined} placeholder={placeholder} disabled={disabled} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: font, color: T.text, background: disabled ? T.surfaceAlt : T.surface, boxSizing: 'border-box', outline: 'none' }} {...rest} />
    }
  </div>
);

export const Select = ({ label, value, onChange, options, ...rest }) => (
  <div style={{ marginBottom: 12 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: T.textSecondary, marginBottom: 4 }}>{label}</label>}
    <select value={value} onChange={typeof onChange === 'function' ? (rest.raw ? onChange : e => onChange(e.target.value)) : undefined} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: font, color: T.text, background: T.surface, boxSizing: 'border-box' }} {...rest}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export const KV = ({ label, value }) => <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, lineHeight: 2 }}><span style={{ color: T.textMuted }}>{label}</span><span style={{ color: T.text, fontWeight: 500 }}>{value}</span></div>;

export const StatusBadge = ({ status }) => { const m = { DATA_COLLECTION: ['Data Collection', 'blue'], SCREENING: ['Screening', 'purple'], RM_REVIEW: ['RM Review', 'amber'], APPROVED: ['Approved', 'green'], REJECTED: ['Rejected', 'red'], REVIEW_DUE: ['Review Due', 'amber'], UNDER_REVIEW: ['Under Review', 'purple'], CLOSED: ['Closed', 'gray'] }; const [l, c] = m[status] || [status, 'gray']; return <Badge color={c}>{l}</Badge>; };

export const RiskBadge = ({ rating }) => { const m = { LOW: 'green', MEDIUM: 'amber', HIGH: 'red', PROHIBITED: 'red' }; return rating ? <Badge color={m[rating] || 'gray'}>{rating}</Badge> : null; };

export const FSBadge = ({ s }) => { const m = { DRAFT: ['Draft', 'gray'], APPROVED: ['Approved', 'blue'], ACTIVE: ['Active', 'green'], EXPIRED: ['Expired', 'amber'], DEFAULTED: ['Defaulted', 'red'], CLOSED: ['Closed', 'gray'] }; const [l, c] = m[s] || [s, 'gray']; return <Badge color={c}>{l}</Badge>; };

export const StBadge = ({ s }) => { const m = { STAGE_1: ['Stage 1', 'green'], STAGE_2: ['Stage 2', 'amber'], STAGE_3: ['Stage 3', 'red'] }; const [l, c] = m[s] || [s, 'gray']; return <Badge color={c}>{l}</Badge>; };

export const DataTable = ({ headers, rows, compact }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 12 : 13, fontFamily: font }}>
      <thead><tr>{headers.map((h, i) => <th key={i} style={{ textAlign: 'left', padding: compact ? '6px 8px' : '8px 10px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((row, i) => <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>{row.map((cell, j) => <td key={j} style={{ padding: compact ? '5px 8px' : '8px 10px', color: T.text }}>{cell}</td>)}</tr>)}
        {rows.length === 0 && <tr><td colSpan={headers.length} style={{ padding: 20, textAlign: 'center', color: T.textMuted }}>No data</td></tr>}
      </tbody>
    </table>
  </div>
);

export const StatCard = ({ label, value, sub, color, onClick, active }) => (
  <Card style={{
    flex: 1, minWidth: 140, padding: 14, textAlign: 'center',
    cursor: onClick ? 'pointer' : undefined,
    transition: onClick ? 'box-shadow 0.15s, border-color 0.15s' : undefined,
    borderColor: active ? (color || T.blue) : undefined,
    boxShadow: active ? `0 0 0 2px ${(color || T.blue)}33` : undefined,
  }} onClick={onClick}>
    <div style={{ fontSize: 24, fontWeight: 700, color: color || T.text }}>{value}</div>
    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{label}</div>
    {sub && <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>{sub}</div>}
  </Card>
);

export const typeColor = t => ({ ASSET: T.blue, LIABILITY: T.purple, EQUITY: T.green, INCOME: T.green, EXPENSE: T.red }[t] || T.textMuted);
export const statusColorFn = s => ({ ACTIVE: T.green, PENDING: T.amber, DORMANT: T.textMuted, FROZEN: T.red, CLOSING: T.amber, CLOSED: T.textMuted }[s] || T.textMuted);
export const dirColor = d => d === 'OUTBOUND' ? 'red' : 'green';
export const schemeColor = s => ({ FPS: 'blue', CHAPS: 'purple', BACS: 'amber', SWIFT: 'red', INTERNAL: 'gray' }[s] || 'gray');
export const pmtStatusColor = s => ({ INITIATED: 'gray', VALIDATED: 'blue', PENDING_APPROVAL: 'amber', REFERRED: 'purple', SUBMITTED: 'amber', ACCEPTED: 'blue', SETTLED: 'green', REJECTED: 'red', FAILED: 'red', RETURNED: 'amber' }[s] || 'gray');

// Aliases used in some modules
export const Inp = Input;
export const Sel = Select;
export const ST = SectionTitle;
